import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, Box, Layers, Move, Target } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Every Element is a Box",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                    <h3 className="font-bold text-xl text-orange-800 mb-3">Square Pegs</h3>
                    <p className="text-slate-700 mb-4">
                        In web design, <strong>everything</strong> is a rectangle. Even circular buttons are just rectangles with rounded corners!
                    </p>
                    <p className="text-slate-700">
                        We call this the <strong>Box Model</strong>. It has 4 layers, like an onion.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The 4 Layers",
        icon: "🧅",
        content: (
            <div className="space-y-6">
                <ul className="space-y-4">
                    <li className="flex items-center gap-4 bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <div className="bg-blue-500 w-8 h-8 rounded shrink-0"></div>
                        <div>
                            <strong className="text-blue-900 block">1. Content</strong>
                            <span className="text-slate-600 text-sm">The actual text or image.</span>
                        </div>
                    </li>
                    <li className="flex items-center gap-4 bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="bg-green-500 w-8 h-8 rounded shrink-0"></div>
                        <div>
                            <strong className="text-green-900 block">2. Padding</strong>
                            <span className="text-slate-600 text-sm">Space INSIDE the box (breathing room).</span>
                        </div>
                    </li>
                    <li className="flex items-center gap-4 bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                        <div className="bg-yellow-500 w-8 h-8 rounded shrink-0"></div>
                        <div>
                            <strong className="text-yellow-900 block">3. Border</strong>
                            <span className="text-slate-600 text-sm">The frame or outline.</span>
                        </div>
                    </li>
                    <li className="flex items-center gap-4 bg-orange-50 p-4 rounded-xl border border-orange-200">
                        <div className="bg-orange-500 w-8 h-8 rounded shrink-0"></div>
                        <div>
                            <strong className="text-orange-900 block">4. Margin</strong>
                            <span className="text-slate-600 text-sm">Space OUTSIDE the box (pushes neighbors away).</span>
                        </div>
                    </li>
                </ul>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🎯",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-100 p-6 rounded-2xl border-2 border-slate-300">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">Copy the Design</h3>
                    <p className="text-slate-700 mb-4">
                        Match the target values to pass the module.
                    </p>
                    <div className="grid grid-cols-3 gap-2 font-mono text-sm text-center">
                        <div className="bg-green-100 text-green-800 p-2 rounded">Padding: 50px</div>
                        <div className="bg-yellow-100 text-yellow-800 p-2 rounded">Border: 15px</div>
                        <div className="bg-orange-100 text-orange-800 p-2 rounded">Margin: 40px</div>
                    </div>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const BoxGame = ({ onComplete }: { onComplete: () => void }) => {
    const [padding, setPadding] = useState(20);
    const [margin, setMargin] = useState(20);
    const [border, setBorder] = useState(5);
    const target = { p: 50, m: 40, b: 15 };

    useEffect(() => {
        if (padding === target.p && margin === target.m && border === target.b) {
            onComplete();
        }
    }, [padding, margin, border]);

    return (
        <div className="flex flex-col gap-8">
            <div className="flex justify-center">
                <div className="inline-block bg-white p-4 rounded-xl shadow-lg border-2 border-pink-200 animate-fade-in-up">
                    <h3 className="flex items-center gap-2 font-bold text-slate-700 mb-2 justify-center">
                        <Target className="text-red-500" /> Target Values
                    </h3>
                    <div className="flex gap-4 text-sm font-mono">
                        <div className={padding === target.p ? "text-green-600 font-bold" : "text-slate-500"}>
                            Padding: {target.p}px {padding === target.p && "✅"}
                        </div>
                        <div className={margin === target.m ? "text-green-600 font-bold" : "text-slate-500"}>
                            Margin: {target.m}px {margin === target.m && "✅"}
                        </div>
                        <div className={border === target.b ? "text-green-600 font-bold" : "text-slate-500"}>
                            Border: {target.b}px {border === target.b && "✅"}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
                {/* LEFT: Controls */}
                <div className="bg-white p-8 rounded-3xl shadow-xl border-4 border-pink-100 space-y-8 relative z-10">
                    <div className="absolute top-0 right-0 opacity-10 pointer-events-none p-4">
                        <img src="/assets/gorillas/gorilla_artist.png" className="w-32" onError={(e) => e.currentTarget.style.display = 'none'} />
                    </div>

                    {/* Content (Implicit) */}
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <h3 className="font-bold text-blue-800 flex items-center gap-2"><Box /> Content</h3>
                        <p className="text-xs text-blue-600">The actual stuff (Text, Image) inside.</p>
                    </div>

                    {/* Padding Control */}
                    <div>
                        <label className="flex justify-between font-bold text-green-600 mb-2">
                            <div className="flex items-center gap-2"><Layers /> Padding (Inside)</div>
                            <span>{padding}px</span>
                        </label>
                        <input
                            type="range" min="0" max="100" step="5"
                            value={padding} onChange={e => setPadding(Number(e.target.value))}
                            className="w-full accent-green-500 cursor-pointer"
                        />
                    </div>

                    {/* Border Control */}
                    <div>
                        <label className="flex justify-between font-bold text-yellow-600 mb-2">
                            <div className="flex items-center gap-2"><Box /> Border (The Frame)</div>
                            <span>{border}px</span>
                        </label>
                        <input
                            type="range" min="0" max="25" step="1"
                            value={border} onChange={e => setBorder(Number(e.target.value))}
                            className="w-full accent-yellow-500 cursor-pointer"
                        />
                    </div>

                    {/* Margin Control */}
                    <div>
                        <label className="flex justify-between font-bold text-orange-600 mb-2">
                            <div className="flex items-center gap-2"><Move /> Margin (Outside)</div>
                            <span>{margin}px</span>
                        </label>
                        <input
                            type="range" min="0" max="100" step="5"
                            value={margin} onChange={e => setMargin(Number(e.target.value))}
                            className="w-full accent-orange-500 cursor-pointer"
                        />
                    </div>
                </div>

                {/* RIGHT: Visualizer */}
                <div className="lg:col-span-2 bg-slate-200 rounded-3xl p-12 flex items-center justify-center min-h-[500px] shadow-inner relative overflow-hidden border-4 border-slate-300">
                    {/* Rulers/Grid bg */}
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

                    <div className="relative">
                        {/* Margin Box (Orange) */}
                        <motion.div
                            layout
                            className="bg-orange-300/30 border-2 border-orange-400 border-dashed flex items-center justify-center relative transition-all duration-300"
                            style={{ padding: margin }}
                        >
                            <div className="absolute top-2 left-2 text-xs font-bold text-orange-600 uppercase">Margin</div>

                            {/* Border Box (Yellow) */}
                            <motion.div
                                layout
                                className="bg-yellow-300 border-yellow-500 flex items-center justify-center relative shadow-xl transition-all duration-300"
                                style={{ borderWidth: border, borderStyle: 'solid' }}
                            >
                                {/* Padding Box (Green) */}
                                <motion.div
                                    layout
                                    className="bg-green-300/30 border-2 border-green-400 border-dashed flex items-center justify-center relative transition-all duration-300"
                                    style={{ padding: padding }}
                                >
                                    <div className="absolute top-1 left-1 text-[10px] font-bold text-green-700 uppercase">Padding</div>

                                    {/* Content Box (Blue) */}
                                    <motion.div
                                        layout
                                        className="bg-blue-500 text-white font-bold p-8 rounded shadow-lg w-48 h-32 flex flex-col items-center justify-center text-center z-10"
                                    >
                                        <div className="text-xl">My Content</div>
                                        <div className="text-xs opacity-75 mt-1">I am really "The Box"</div>
                                    </motion.div>

                                </motion.div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const BoxModel = () => {
    // Optimized selector to prevent re-renders from unrelated store changes
    const completeModule = useAcademyStore(state => state.completeModule);
    const [completed, setCompleted] = useState(false);

    const handleComplete = React.useCallback(() => {
        setCompleted(true);
        completeModule('box-model', 100);
    }, [completeModule]);

    return (
        <LessonWrapper
            moduleName="Box Model"
            moduleEmoji="🎨"
            moduleColor="orange"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Design Studio"
        >
            <BoxGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default BoxModel;
